import { useState, useEffect } from 'react';
import WhiteLogo from "../../../images/white-logo.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
// import SettingsIcon from "../../../images/settings-icon.svg";
import ChartIcon from "../../../images/icon-header-faq.png";
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import axios from 'axios';
import apiConfig from '../config/apiConfig';
import { API_ENDPOINTS } from '../Auth/utils/constants.js';
import { hasUnsavedGiftProgress, showNavigationConfirmation } from '../Gift/navigationGuard';
import { useGiftContext } from '../Gift/GiftContext';
import NotificationDropdown from './NotificationDropdown';
import ProfileDropdown from './ProfileDropdown';
import NewProfileUser from '../../../images/new-user-profile-icon.png';
import { useNotifications } from './hooks/useNotifications';
import "./Header.scss";

// Utility function to generate initials
const generateInitials = (firstName: any, lastName: any, fullName?: any) => {
    // If both first and last name are available
    if (firstName && lastName) {
        return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
    }
    // If only first name is available, use first two letters
    if (firstName && firstName.length >= 2) {
        return `${firstName.charAt(0).toUpperCase()}${firstName.charAt(1).toUpperCase()}`;
    }
    // If only first name with one letter
    if (firstName) {
        return firstName.charAt(0).toUpperCase();
    }
    // If only last name is available
    if (lastName && lastName.length >= 2) {
        return `${lastName.charAt(0).toUpperCase()}${lastName.charAt(1).toUpperCase()}`;
    }
    if (lastName) {
        return lastName.charAt(0).toUpperCase();
    }
    // Fallback to fullName if firstName/lastName not available
    if (fullName) {
        const names = fullName.trim().split(/\s+/);
        if (names.length >= 2) {
            return `${names[0].charAt(0).toUpperCase()}${names[names.length - 1].charAt(0).toUpperCase()}`;
        } else if (names.length === 1 && names[0].length >= 2) {
            return `${names[0].charAt(0).toUpperCase()}${names[0].charAt(1).toUpperCase()}`;
        } else if (names.length === 1) {
            return names[0].charAt(0).toUpperCase();
        }
    }
    // Default fallback if no name is available
    return <img src={NewProfileUser} alt="User" />;
};

// Utility function to truncate full name
const truncateFullName = (fullName: any, maxLength = 15) => {
    if (fullName.length <= maxLength) return fullName;
    return `${fullName.substring(0, maxLength)}...`;
};

const Header = ({ activeTab, toggle }: { activeTab?: any; toggle?: any } = {}) => {
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [userProfile, setUserProfile] = useState({
        fullName: "",
        firstName: "",
        lastName: "",
        email: "",
        accountStatus: "",
        profileImage: ""
    });
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    // Initialize notifications hook
    const {
        notifications,
        unreadCount,
        loading: notificationsLoading,
        markAsRead,
        markAllAsRead,
        refreshNotifications
    } = useNotifications();
    
    // Try to get gift context, but handle case where it might not be available
    let giftContext = null;
    try {
        giftContext = useGiftContext();
    } catch (error) {
        // Gift context not available (we're not in a gift creation route)
        // This is normal behavior outside of gift creation pages
    }

    // Check if we're in gift creation flow (both new and legacy URLs)
    const isInGiftCreation = location.pathname.includes('/start-gift') ||
                            location.pathname.includes('/my-gifts/create/') ||
                            location.pathname.includes('/my-gifts/edit/') ||
                            location.pathname.includes('/new-gift') ||
                            location.pathname.includes('/create-gift');

    // Check if we're on subscription page or user hasn't subscribed yet
    const isOnSubscriptionPage = location.pathname === '/subscription';
    const hasActiveSubscription = userProfile.accountStatus === 'Active';
    const shouldShowNavItems = !isOnSubscriptionPage && hasActiveSubscription;

    // Fetch user profile on component mount
    useEffect(() => {
        fetchUserProfile();
    }, []);

    // Listen for profile updates
    useEffect(() => {
        const handleProfileUpdate = () => {
            fetchUserProfile();
        };

        // Listen for custom profile update event
        window.addEventListener('profileUpdated', handleProfileUpdate);

        return () => {
            window.removeEventListener('profileUpdated', handleProfileUpdate);
        };
    }, []);

    const fetchUserProfile = async () => {
        try {
            const token = localStorage.getItem('authToken');

            if (!token) {
                setLoading(false);
                return;
            }

            const response = await axios.get(
                `${apiConfig.apiBaseURL}/details`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            // Update profile data if available
            if (response.data && response.data.data) {
                const apiData = response.data.data;

                // Handle user details from the API response structure
                if (apiData.userDetails) {
                    const userDetails = apiData.userDetails;

                    const capitalizeFirstLetter = (str: string) =>
                        str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

                    const capitalizedFirstName = capitalizeFirstLetter(userDetails.firstName || "");
                    const capitalizedLastName = capitalizeFirstLetter(userDetails.lastName || "");

                    setUserProfile((prev) => ({
                        ...prev,
                        fullName: capitalizedFirstName && capitalizedLastName
                            ? `${capitalizedFirstName} ${capitalizedLastName}`
                            : capitalizedFirstName || "",
                        firstName: capitalizedFirstName,
                        lastName: capitalizedLastName,
                        email: userDetails.email || "",
                        profileImage: userDetails.profileUrl || userDetails.profileImage || "",
                        accountStatus: userDetails.accountStatus || "",
                    }));
                }
            }
        } catch (error) {
            // Keep default values on error
        } finally {
            setLoading(false);
        }
    };

    const handleLogoutClick = (e: any) => {
        e.preventDefault();
        setShowLogoutModal(true);
    };

    const handleLogoutConfirm = () => {
        // Clear all local storage
        localStorage.clear();
        
        // Clear session storage as well (optional)
        sessionStorage.clear();
        
        // Close modal
        setShowLogoutModal(false);
        
        // Navigate to login page
        navigate('/');
    };

    const handleLogoutCancel = () => {
        setShowLogoutModal(false);
    };

    // Handle logo navigation with navigation guard integration
    const handleLogoClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault(); // Prevent default Link behavior
        
        // If not in gift creation, navigate directly
        if (!isInGiftCreation) {
            navigate('/my-gifts');
            return;
        }
        
        // Check for unsaved progress directly using gift context
        const hasProgress = giftContext ? hasUnsavedGiftProgress(
            giftContext.currentGift, 
            true, 
            giftContext.activeTab
        ) : false;
        
        if (!hasProgress) {
            // No progress to save, navigate directly
            navigate('/my-gifts');
            return;
        }
        
        // Has progress, show the navigation guard modal
        // console.log('🛡️ Progress detected, showing navigation confirmation...');
        try {
            const result = await showNavigationConfirmation({
                currentGift: giftContext?.currentGift,
                isInGiftCreation: true,
                saveGiftAsDraft: giftContext?.saveGiftAsDraft || (() => Promise.resolve()),
                navigate: navigate,
                targetPath: '/my-gifts',
                activeTab: giftContext?.activeTab
            });
            
            // console.log('🛡️ Navigation confirmation completed:', result);
        } catch (error) {
            // console.error('🛡️ Error in navigation confirmation:', error);
            // Fallback to direct navigation
            navigate('/my-gifts');
        }
    };

    // Generate initials for potential avatar use
    const userInitials = generateInitials(
        userProfile.firstName,
        userProfile.lastName,
        userProfile.fullName
    );

    // Get truncated display name
    const displayName = truncateFullName(userProfile.fullName);

    return (
      <>
        <header className="header">
          {/* Main Header */}
          <div className="header-main">
            <div className="container">
              <div className="d-flex justify-content-between align-items-center">
                <aside className="header-left d-flex align-items-center">
                  <figure className="mb-0 me-4">
                    <Link to="/my-gifts" onClick={handleLogoClick}>
                      <img src={WhiteLogo} alt="Logo" />
                    </Link>
                  </figure>
                </aside>

                {/* Nav tabs inside header for large screens (991px+) */}
                {shouldShowNavItems && (
                  <nav className="header-nav-large">
                    <Link
                      to="/my-gifts"
                      className={`header-nav-link ${location.pathname === '/my-gifts' || location.pathname.startsWith('/my-gifts/') ? 'active' : ''}`}
                    >
                      My Gifts & Messages
                    </Link>
                    <Link
                      to="/my-contacts"
                      className={`header-nav-link ${location.pathname === '/my-contacts' ? 'active' : ''}`}
                    >
                      My Contacts
                    </Link>
                  </nav>
                )}

                <aside className="header-right d-flex align-items-center">
                  <ul className="d-flex list-unstyled mb-0 me-3">
                    {/* <li className="me-3">
                      <Link to="/">
                        <img src={SettingsIcon} alt="Settings" />
                      </Link>
                    </li> */}
                    <li className="me-3">
                      <OverlayTrigger
                        placement="bottom"
                        overlay={
                          <Tooltip id="notification-tooltip">
                            Notifications
                          </Tooltip>
                        }
                      >
                        <div>
                          <NotificationDropdown
                            notifications={notifications}
                            unreadCount={unreadCount}
                            loading={notificationsLoading}
                            onMarkAsRead={markAsRead}
                            onMarkAllAsRead={markAllAsRead}
                            onRefresh={refreshNotifications}
                          />
                        </div>
                      </OverlayTrigger>
                    </li>
                    <li>
                      <OverlayTrigger
                        placement="bottom"
                        overlay={
                          <Tooltip id="help-support-tooltip">
                            Help & Support
                          </Tooltip>
                        }
                      >
                        <Link to="/help-support">
                          <img src={ChartIcon} alt="Help & Support" />
                        </Link>
                      </OverlayTrigger>
                    </li>
                  </ul>
                  <ProfileDropdown
                    userInitials={userInitials}
                    displayName={displayName}
                    profileImage={userProfile.profileImage}
                    loading={loading}
                    onLogout={handleLogoutClick}
                  />
                </aside>
              </div>
            </div>
          </div>

          {/* Sub Navigation - Only show on smaller screens (below 991px) */}
          {shouldShowNavItems && (
            <div className="sub-navigation">
              <div className="container">
                <nav className="sub-nav-links">
                  <Link
                    to="/my-gifts"
                    className={`sub-nav-link ${location.pathname === '/my-gifts' || location.pathname.startsWith('/my-gifts/') ? 'active' : ''}`}
                  >
                    My Gifts & Messages
                  </Link>
                  <Link
                    to="/my-contacts"
                    className={`sub-nav-link ${location.pathname === '/my-contacts' ? 'active' : ''}`}
                  >
                    My Contacts
                  </Link>
                </nav>
              </div>
            </div>
          )}
        </header>

        {/* Logout Confirmation Modal - Updated to match subscription modal structure */}
        <Modal
          show={showLogoutModal}
          onHide={handleLogoutCancel}
          centered
          className="delete-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Logout</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ color: "#677289", maxWidth: "80%", margin: "0 auto" }}>
              <b>Are you sure you want to logout?</b>
            </p>
            <p style={{ color: "#677289", maxWidth: "80%", margin: "0 auto" }}>
              <b>You will need to sign in again to access your account.</b>
            </p>
          </Modal.Body>
          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "center",
              padding: "0 0 20px 0",
            }}
          >
            <Button
              variant="secondary"
              onClick={handleLogoutCancel}
              style={{ width: "30%", margin: "0" }}
            >
              Cancel
            </Button>
            <Button
              className="bttn btn btn-primary"
              onClick={handleLogoutConfirm}
              style={{ width: "30%", margin: "0" }}
            >
              Logout
            </Button>
          </div>
        </Modal>
      </>
    );
};

export default Header;